import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { LeedService } from 'src/app/service/leed.service';
import { saveAs } from 'file-saver';

interface LeadDetalheCache {
  carregando: boolean;
  detalhe: any;
  historico: any[];
  erro: string;
  carregado: boolean;
}

@Component({
  selector: 'app-leeds-distribuidor',
  templateUrl: './leeds-distribuidor.component.html',
  styleUrls: ['./leeds-distribuidor.component.css']
})
export class LeedsDistribuidorComponent implements OnInit, OnDestroy {
  carregando = false;
  salvandoStatusId = '';
  salvandoMotivoId = '';
  exportando = false;
  busca = '';
  statusFiltro = '';
  mensagem = '';
  erro = '';

  leads: any[] = [];
  detalheCachePorLead: { [leedId: string]: LeadDetalheCache } = {};
  totais = {
    novosLeads: 0,
    emAndamento: 0,
    captacaoEfetuada: 0,
    cadastroReprovado: 0,
    naoConvertido: 0
  };

  private readonly buscaSubject = new Subject<string>();

  statusOpcoes = [
    'Novos Leads',
    'Em Andamento',
    'Captação Efetuada',
    'Cadastro Reprovado',
    'Não Convertido'
  ];

  constructor(private leedService: LeedService) { }

  ngOnInit() {
    this.buscaSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((valor: string) => {
        this.busca = valor;
        this.carregarDashboard();
      });

    this.carregarDashboard();
  }

  ngOnDestroy() {
    this.buscaSubject.complete();
  }

  carregarDashboard() {
    this.carregando = true;
    this.erro = '';

    this.leedService.getDashboardLeads(this.busca, this.statusFiltro).subscribe((res: any) => {
      this.carregando = false;
      if (res && res.sucesso) {
        this.leads = res.itens || [];
        this.totais = res.totais || this.totais;
      } else {
        this.erro = (res && res.msg) ? res.msg : 'Não foi possível carregar os leads.';
      }
    }, () => {
      this.carregando = false;
      this.erro = 'Não foi possível carregar os leads.';
    });
  }

  onBuscaChange(valor: string) {
    this.buscaSubject.next(valor || '');
  }

  filtrarPorStatus(status: string) {
    this.statusFiltro = status;
    this.carregarDashboard();
  }

  onStatusFiltroChange() {
    this.carregarDashboard();
  }

  exportarExcel() {
    if (this.exportando) {
      return;
    }

    this.exportando = true;
    this.erro = '';

    this.leedService.exportarDashboardLeads(this.busca, this.statusFiltro).subscribe((arquivo: Blob) => {
      this.exportando = false;
      saveAs(arquivo, 'leads.xlsx');
    }, () => {
      this.exportando = false;
      this.erro = 'Não foi possível exportar os leads.';
    });
  }

  atualizarStatus(lead: any) {
    if (!lead || !lead.leedId) {
      return;
    }

    this.salvandoStatusId = lead.leedId;
    this.mensagem = '';
    this.erro = '';
    this.leedService.atualizarDashboardLeadStatus(lead.leedId, lead.status).subscribe((res: any) => {
      this.salvandoStatusId = '';
      if (res && res.sucesso) {
        this.mensagem = 'Status atualizado com sucesso.';
        this.carregarDashboard();
        const cache = this.detalheCachePorLead[lead.leedId];
        if (cache && cache.carregado) {
          cache.detalhe.status = lead.status;
        }
      } else {
        this.erro = (res && res.msg) ? res.msg : 'Falha ao atualizar o status.';
      }
    }, () => {
      this.salvandoStatusId = '';
      this.erro = 'Falha ao atualizar o status.';
    });
  }

  atualizarMotivoDesfesho(lead: any) {
    if (!lead || !lead.leedId) {
      return;
    }

    this.salvandoMotivoId = lead.leedId;
    this.mensagem = '';
    this.erro = '';
    this.leedService.atualizarDashboardLeadMotivoDesfesho(lead.leedId, lead.motivoDesfesho).subscribe((res: any) => {
      this.salvandoMotivoId = '';
      if (res && res.sucesso) {
        this.mensagem = 'Motivo do desfesho atualizado com sucesso.';
        const cache = this.detalheCachePorLead[lead.leedId];
        if (cache && cache.carregado) {
          cache.detalhe.motivoDesfesho = lead.motivoDesfesho;
        }
      } else {
        this.erro = (res && res.msg) ? res.msg : 'Falha ao atualizar motivo do desfesho.';
      }
    }, () => {
      this.salvandoMotivoId = '';
      this.erro = 'Falha ao atualizar motivo do desfesho.';
    });
  }

  onExpandirLead(lead: any) {
    if (!lead || !lead.leedId) {
      return;
    }

    const cache = this.getOuCriaCache(lead.leedId);
    if (cache.carregado || cache.carregando) {
      return;
    }

    cache.carregando = true;
    cache.erro = '';

    forkJoin([
      this.leedService.getDashboardLeadById(lead.leedId),
      this.leedService.getGerenciamentoHistorico(lead.leedId)
    ]).subscribe(([resDetalhe, resHistorico]: any[]) => {
      if (resDetalhe && resDetalhe.sucesso) {
        cache.detalhe = resDetalhe.item;
        cache.carregado = true;
      } else {
        cache.erro = (resDetalhe && resDetalhe.msg) ? resDetalhe.msg : 'Não foi possível carregar os dados completos do cadastro.';
      }

      if (resHistorico && resHistorico.sucesso) {
        cache.historico = resHistorico.tentativas || [];
      } else {
        cache.historico = [];
      }

      cache.carregando = false;
    }, () => {
      cache.carregando = false;
      cache.erro = 'Não foi possível carregar os dados completos do cadastro.';
    });
  }

  getCacheLead(leadId: string): LeadDetalheCache {
    return this.getOuCriaCache(leadId);
  }

  private getOuCriaCache(leadId: string): LeadDetalheCache {
    if (!this.detalheCachePorLead[leadId]) {
      this.detalheCachePorLead[leadId] = {
        carregando: false,
        detalhe: null,
        historico: [],
        erro: '',
        carregado: false
      };
    }

    return this.detalheCachePorLead[leadId];
  }
}
